/**
 * channelButtonModel — EINE reine Funktion, die pro Kanal entscheidet, welcher
 * Connect-Button gezeigt wird (Label, Ton, Klickbarkeit, Hinweis, Kind).
 *
 * Ersetzt die bisher über mehrere verschachtelte JSX-Bedingungen verteilte Logik
 * durch eine testbare Single-Source-of-Truth. Kein React, keine Seiteneffekte.
 */

// PlatformStatus (Backend-Enum) — gespiegelt
export const STATUS = {
  READY: "ready_to_post",
  CONNECTED: "connected",
  NOT_CONNECTED: "not_connected",
  RECONNECT: "reconnect_needed",
  EXPIRED: "token_expired",
  INCOMPLETE: "incomplete_permissions",
  ERROR: "error",
  CONNECTING: "connecting",
};

const CONNECTED_STATES = [STATUS.CONNECTED, STATUS.READY];
const WARN_STATES = [STATUS.RECONNECT, STATUS.EXPIRED, STATUS.INCOMPLETE, STATUS.ERROR];

/** Kunden-Badge je Status (Label + Ton). Keine Dev-Sprache. */
export function getStatusBadge(channel, accountData) {
  const status = accountData?.status || STATUS.NOT_CONNECTED;
  const action = accountData?.recommended_action;
  if (channel?.comingSoon && status === STATUS.NOT_CONNECTED)
    return { label: "In Vorbereitung", tone: "violet" };
  if (status === STATUS.CONNECTING) return { label: "Verbinde…", tone: "neutral" };
  // Verbunden, aber Einrichtung unvollständig (z. B. Token ohne verknüpfte Seite) darf
  // NICHT grün als "Verbunden" erscheinen — check_setup schlägt den connected-Status.
  if (action === "check_setup") return { label: "Einrichtung prüfen", tone: "amber" };
  if (CONNECTED_STATES.includes(status)) return { label: "Verbunden", tone: "green" };
  if (status === STATUS.RECONNECT) return { label: "Verbindung erneuern", tone: "amber" };
  if (status === STATUS.EXPIRED) return { label: "Anmeldung abgelaufen", tone: "red" };
  if (status === STATUS.INCOMPLETE) return { label: "Berechtigung fehlt", tone: "amber" };
  if (status === STATUS.ERROR) return { label: "Fehler", tone: "red" };
  if (action === "configure" || accountData?.is_configured === false)
    return { label: "Einrichtung erforderlich", tone: "amber" };
  return { label: "Nicht verbunden", tone: "neutral" };
}

// Exakter Sperr-Hinweis (Owner-Vorgabe 2026-07-06) — 1:1 auch das 403-Detail des
// Backend-Kanal-Gates. Nicht umformulieren.
export const LOCKED_CHANNEL_LABEL = "nur für gekaufte Pakete";

/**
 * Entscheidet den primären Connect-Button.
 * @param access  Kanal-Zugriff aus GET /billing/plan → channel_access[id]:
 *                { allowed, mode: 'connect'|'assistant'|'locked_upsell'|'locked_expired', message }
 *                undefined → Bestandsverhalten (Owner-Workspace / Plan noch nicht geladen).
 * @returns {{ kind, label, tone, disabled, hint?, useWizard?, assistant? }}
 *   kind: 'connect'|'reconnect'|'connected'|'needs_setup'|'coming_soon'|'wait'|'linked'|'wizard'|'locked'
 *   tone: 'primary'|'amber'|'violet'|'green'|'muted'
 */
export function getConnectButtonModel(channel, accountData, { isConnecting = false, access } = {}) {
  const status = accountData?.status || STATUS.NOT_CONNECTED;
  const action = accountData?.recommended_action;
  const isConfigured = accountData?.is_configured !== false;
  const notConnected = status === STATUS.NOT_CONNECTED;
  const connected = CONNECTED_STATES.includes(status);
  const warn = WARN_STATES.includes(status);

  // Kanal-Gate (Server-Entscheidung, hier nur Anzeige): gesperrt schlägt alles außer
  // "Verbunden" — bestehende Verbindungen bleiben sichtbar/trennbar, neue sind gesperrt.
  // Der Button bleibt klickbar: Klick → Upsell (wird als locked_channel_clicked geloggt).
  if (!connected && access && access.allowed === false)
    return {
      kind: "locked",
      label: access.message || LOCKED_CHANNEL_LABEL,
      tone: "muted",
      disabled: false,
      hint: access.mode === "locked_expired"
        ? "Deine Testphase ist beendet — wähle ein Paket, um Kanäle zu verbinden."
        : "In der Testphase verbindest du Bluesky. Diesen Kanal schaltest du mit einem Paket frei.",
    };

  // Kauf vorhanden → der Verbinden-Einstieg heißt Verbindungsassistent (geführter Flow).
  const assistant = access?.mode === "assistant";

  if (channel?.comingSoon && notConnected)
    return { kind: "coming_soon", label: "Einrichtung erforderlich", tone: "violet", disabled: false,
             hint: "Dieser Kanal wird vorbereitet — externe Freischaltung erforderlich." };

  if (action === "wait")
    return { kind: "wait", label: "Bitte warten", tone: "amber", disabled: true,
             hint: "Plattform vorübergehend nicht erreichbar." };

  if (connected)
    return { kind: "connected", label: "Verbunden", tone: "green", disabled: false };

  // Instagram & Co.: wird über einen anderen Kanal verbunden (kein eigener Login)
  if (notConnected && channel?.linkedTo && channel?.oauthFlow === false)
    return { kind: "linked", label: `Über ${channel.linkedTo} verbinden`, tone: "muted", disabled: true,
             hint: `Wird automatisch mit ${channel.linkedTo} verbunden — kein separater Login nötig.` };

  if (action === "requires_https_tunnel")
    return { kind: "wizard", label: warn ? "Neu verbinden" : "Verbinden", tone: "amber", disabled: false,
             useWizard: true, hint: "Sichere Verbindung wird Schritt für Schritt eingerichtet." };

  // Geführter Wizard (LinkedIn/TikTok)
  if (channel?.useWizard)
    return { kind: "wizard", label: warn ? "Neu verbinden" : (assistant ? "Verbindungsassistent" : "Verbinden"),
             tone: warn ? "amber" : "primary", disabled: false, useWizard: true, assistant };

  if (action === "configure" || !isConfigured)
    return { kind: "needs_setup", label: "Einrichtung erforderlich", tone: "amber", disabled: false,
             hint: "Die Anmeldedaten für diesen Kanal sind noch nicht hinterlegt." };

  if (warn)
    return { kind: "reconnect", label: "Neu verbinden", tone: "amber", disabled: false };

  return { kind: "connect", label: assistant ? "Verbindungsassistent" : "Verbinden",
           tone: "primary", disabled: false, assistant };
}

/**
 * getReconnectActionModel — die Neuverbindung eines Kanals, der noch VERBUNDEN ist.
 *
 * Warum es das braucht (Befund 14.08.2026): `getConnectButtonModel` fängt oben mit
 * `if (connected)` ab, bevor irgendein Reconnect-Zweig erreicht wird — und die Karte
 * rendert für einen verbundenen Kanal nur „Prüfen" und „Trennen". Wer VORSORGLICH neu
 * verbinden will, findet nichts zum Klicken; der Reconnect-Knopf erscheint erst, wenn
 * der Zugang bereits tot ist.
 *
 * Das ist nicht theoretisch: Die LinkedIn-Zugänge (Owner und zahlender Kunde) laufen am
 * 12.09. bzw. 13.09.2026 ab und haben KEIN refresh_token — LinkedIn gibt im Standard-Flow
 * keins heraus. Der einzige Weg zurück ist ein vollständiger OAuth-Durchlauf, und der
 * einzige Weg dorthin war bisher „Trennen": das löscht den noch gültigen Zugang, BEVOR
 * der neue existiert. Scheitert danach die Anmeldung, ist der Kanal sofort still statt
 * erst am Ablauftag — bei LinkedIn dem einzigen Kanal, der nachweislich Besucher bringt.
 *
 * Ein zweiter Durchlauf ist gefahrlos: Der Callback ersetzt die Zeile per Upsert über
 * (platform, platform_user_id, workspace_id) — es entsteht keine Lücke.
 *
 * @returns {{ label, useWizard }|null} — null heißt: für diesen Kanal gibt es hier nichts
 *   anzubieten (nicht verbunden, kein eigener Login, oder gesperrt).
 */
export function getReconnectActionModel(channel, accountData, { access } = {}) {
  const status = accountData?.status || STATUS.NOT_CONNECTED;
  if (!CONNECTED_STATES.includes(status)) return null;

  // Instagram & Co. haben keinen eigenen Login — sie hängen an einem anderen Kanal.
  // Ein „Neu verbinden" hier würde ins Leere führen.
  if (channel?.linkedTo && channel?.oauthFlow === false) return null;

  // Gesperrter Kanal: Eine bestehende Verbindung bleibt sichtbar und trennbar, aber ein
  // neuer Verbindungsvorgang ist genau das, was das Kanal-Gate verhindert (der Server
  // würde 403en). Gleiche Linie wie im primären Button.
  if (access && access.allowed === false) return null;

  return { label: "Neu verbinden", useWizard: !!channel?.useWizard };
}
